import type { EditorView } from '@tiptap/pm/view';
import { TextSelection } from '@tiptap/pm/state';

export const CODE_BLOCK_TAB_SIZE = 2;

function indent(spaces: number) {
  return ' '.repeat(spaces);
}

export function handleCodeBlockTab(view: EditorView, event: KeyboardEvent): boolean {
  if (event.key !== 'Tab') return false;

  const { state } = view;
  const { selection } = state;
  const { $from, empty } = selection;
  if ($from.parent.type.name !== 'codeBlock') return false;

  event.preventDefault();
  const tabSize = CODE_BLOCK_TAB_SIZE;
  const pad = indent(tabSize);

  if (!event.shiftKey) {
    if (empty) {
      view.dispatch(state.tr.insertText(pad));
      return true;
    }

    const { from, to } = selection;
    const text = state.doc.textBetween(from, to, '\n', '\n');
    const indentedText = text
      .split('\n')
      .map(line => pad + line)
      .join('\n');
    view.dispatch(state.tr.insertText(indentedText, from, to));
    return true;
  }

  if (empty) {
    const { pos } = $from;
    const codeBlockStart = $from.start();
    const codeBlockEnd = $from.end();
    const allText = state.doc.textBetween(codeBlockStart, codeBlockEnd, '\n', '\n');
    const lines = allText.split('\n');

    let currentLineIndex = 0;
    let charCount = 0;
    const relativeCursorPos = pos - codeBlockStart;

    for (let i = 0; i < lines.length; i += 1) {
      if (charCount + lines[i].length >= relativeCursorPos) {
        currentLineIndex = i;
        break;
      }
      charCount += lines[i].length + 1;
    }

    const currentLine = lines[currentLineIndex];
    const leadingSpaces = currentLine.match(/^ */)?.[0] || '';
    const spacesToRemove = Math.min(leadingSpaces.length, tabSize);
    if (spacesToRemove === 0) return true;

    let lineStartPos = codeBlockStart;
    for (let i = 0; i < currentLineIndex; i += 1) {
      lineStartPos += lines[i].length + 1;
    }

    const tr = state.tr.delete(lineStartPos, lineStartPos + spacesToRemove);
    const cursorPosInLine = pos - lineStartPos;
    if (cursorPosInLine <= spacesToRemove) {
      tr.setSelection(TextSelection.create(tr.doc, lineStartPos));
    }
    view.dispatch(tr);
    return true;
  }

  const { from, to } = selection;
  const text = state.doc.textBetween(from, to, '\n', '\n');
  const reverseIndentText = text
    .split('\n')
    .map(line => {
      const leadingSpaces = line.match(/^ */)?.[0] || '';
      const spacesToRemove = Math.min(leadingSpaces.length, tabSize);
      return line.slice(spacesToRemove);
    })
    .join('\n');
  view.dispatch(state.tr.insertText(reverseIndentText, from, to));
  return true;
}
